<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue';
import { Capacitor } from '@capacitor/core';
import { IonAlert } from '@ionic/vue';

import { BASE_WRAPPER_CONFIG } from '@/constants';
import {
  getConsentCookie,
  getConsentPreferences,
} from '@/helper/cookie-consent/storage';
import matomoHelper from '@/helper/matomo';

const showNativeAppModal = ref(false);
const platform = Capacitor.getPlatform();

function hasStoredConsent(): boolean {
  return getConsentPreferences() !== null || getConsentCookie() !== null;
}

function shouldShowModal(): boolean {
  const lastShown = localStorage.getItem('nativeAppModalShown');
  const dayInMs = 24 * 60 * 60 * 1000;

  return (
    platform !== 'ios' &&
    platform !== 'android' &&
    (lastShown === null || Date.now() - dayInMs > Number(lastShown))
  );
}

function openNativeAppModal(): void {
  if (!shouldShowModal()) {
    return;
  }
  showNativeAppModal.value = true;
}

function scheduleOpenNativeAppModal(delayMs: number): void {
  setTimeout(openNativeAppModal, delayMs);
}

let consentListener: (() => void) | null = null;

onMounted(() => {
  if (hasStoredConsent()) {
    scheduleOpenNativeAppModal(BASE_WRAPPER_CONFIG.MODAL_SHOW_DELAY_MS);
    return;
  }

  consentListener = () => {
    scheduleOpenNativeAppModal(
      BASE_WRAPPER_CONFIG.POST_CONSENT_MODAL_DELAY_MS,
    );
    if (consentListener) {
      window.removeEventListener('consentChanged', consentListener);
      consentListener = null;
    }
  };
  window.addEventListener('consentChanged', consentListener);
});

onUnmounted(() => {
  if (consentListener) {
    window.removeEventListener('consentChanged', consentListener);
  }
});

const buttons = [
  {
    text: 'Zum Store',
    handler: async () => {
      localStorage.setItem('nativeAppModalShown', Date.now().toString());
      // redirect to store
      let isIOS =
        /iPhone|iPad|iPod/i.test(navigator.userAgent) ||
        (navigator.userAgent.includes('Mac') && navigator.maxTouchPoints > 1);

      let storeUrl = isIOS
        ? 'https://apps.apple.com/de-de/app/ukk-aktuell-uniklinik-köln/id1575419811'
        : 'https://play.google.com/store/apps/details?id=com.sunzinet.ukkaktuell&hl=de';

      await matomoHelper.trackEvent({
        e_c: 'PWA Store Modal',
        e_a: 'Choice',
        e_n: `Store Redirect (${isIOS ? 'iOS' : 'Android'})`,
        e_v: 1,
      });

      setTimeout(() => {
        window.location.replace(storeUrl);
      }, BASE_WRAPPER_CONFIG.REDIRECT_DELAY_MS);
    },
  },
  {
    text: 'Weiter mit Web-Version',
    role: 'cancel',
    cssClass: 'secondary',
    handler: async () => {
      localStorage.setItem('nativeAppModalShown', Date.now().toString());
      await matomoHelper.trackEvent({
        e_c: 'PWA Store Modal',
        e_a: 'Choice',
        e_n: 'Web',
        e_v: 1,
      });
    },
  },
];
</script>

<template>
  <ion-alert
    :is-open="showNativeAppModal"
    header="Jetzt App im Store installieren"
    message="Die Uniklinik Köln App steht ab sofort im Apple App Store und Google Play Store zur Verfügung."
    css-class="my-custom-class"
    :buttons="buttons"
    @did-dismiss="showNativeAppModal = false"
  >
  </ion-alert>
</template>
